import { Box, MenuItem, Select, Stack, Typography } from "@mui/material";
import { regionKey } from "utils/regionKey";
import ColorSwatch from "./ColorSwatch";
import { useLidarClearance } from "./LidarClearanceContext";
import type { AxisProperties } from "./api";
import {
  NO_ISSUE_KEY,
  SEVERITY_ROWS,
  type SeverityKey,
  classifySeverity,
  withValueSeverity,
} from "./axisSeverity";
import { colorForValue } from "./colorScaleDisplay";
import t from "./translations";

type Props = {
  region: string;
  value: AxisProperties;
  onChange: (value: AxisProperties) => void;
  disabled?: boolean;
};

export default function AxisSeverityValueList({
  region,
  value,
  onChange,
  disabled,
}: Props) {
  const { colorScaleByKey } = useLidarClearance();
  const colorScale = colorScaleByKey.get(regionKey(region, value.color_scale));

  const axisValues = Array.from(
    { length: Math.max(0, value.max_value - value.min_value + 1) },
    (_, index) => value.min_value + index
  );

  return (
    <Box
      sx={{
        maxHeight: 360,
        overflowY: "auto",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
      data-testid="axis-severity-values"
    >
      {axisValues.map((axisValue) => {
        const severityKey: SeverityKey =
          classifySeverity(value, axisValue)?.key ?? NO_ISSUE_KEY;
        return (
          <Stack
            key={axisValue}
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", px: 1.5, py: 0.5 }}
            data-testid={`axis-severity-values.value-${axisValue}`}
          >
            <ColorSwatch color={colorForValue(colorScale, axisValue)} />
            <Typography variant="body2" sx={{ width: 32 }}>
              {axisValue}
            </Typography>
            <Select
              variant="standard"
              size="small"
              value={severityKey}
              onChange={(event) =>
                onChange(
                  withValueSeverity(
                    value,
                    axisValue,
                    event.target.value as SeverityKey
                  )
                )
              }
              disabled={disabled}
              sx={{ flexGrow: 1 }}
            >
              {SEVERITY_ROWS.map((row) => (
                <MenuItem key={row.key} value={row.key}>
                  {row.label}
                </MenuItem>
              ))}
              <MenuItem value={NO_ISSUE_KEY}>{t.SeverityNoIssueLabel}</MenuItem>
            </Select>
          </Stack>
        );
      })}
    </Box>
  );
}
